import React, { useEffect, useRef, useState } from "react";
import { t } from "../../i18n";
import AnimatedSection from "../AnimatedSection/AnimatedSection";

const tabs = [
  {
    label: t("tabSection.label1"),
    sectionBg: "bg-primaryGreen-extralight",
    contentBg: "bg-silver",
    textColor: "text-black",
    title: t("tabSection.title1"),
    subtitle: t("tabSection.subtitle1"),
    description: t("tabSection.description1"),
    image: "/images/menu-1.jpg",
  },
  {
    label: t("tabSection.label2"),
    sectionBg: "bg-[#FFDDC1]",
    contentBg: "bg-[#ffe9d9]",
    textColor: "text-black",
    title: t("tabSection.title2"),
    subtitle: t("tabSection.subtitle2"),
    description: t("tabSection.description2"),
    image: "/images/menu-2.jpg",
  },
  {
    label: t("tabSection.label3"),
    sectionBg: "bg-[#8c52ff]",
    contentBg: "bg-[#9662ff]",
    textColor: "text-white",
    title: t("tabSection.title3"),
    subtitle: t("tabSection.subtitle3"),
    description: t("tabSection.description3"),
    image: "/images/menu-3.jpg",
  },
  {
    label: t("tabSection.label4"),
    sectionBg: "bg-[#5e77e8]",
    contentBg: "bg-[#667ee9]",
    textColor: "text-white",
    title: t("tabSection.title4"),
    subtitle: t("tabSection.subtitle4"),
    description: t("tabSection.description4"),
    image: "/images/menu-4.jpg",
  },
];

const TabSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const current = tabs[activeTab];

  const sectionRef = useRef<HTMLDivElement>(null);
  const [hasActivated, setHasActivated] = useState(false);

  // Touch/swipe için state'ler
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasActivated) {
          setHasActivated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      observer.disconnect();
    };
  }, []); // hasActivated dependency'sini kaldırdım

  // Touch/swipe fonksiyonları
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && activeTab < tabs.length - 1) {
      setActiveTab(activeTab + 1);
    }
    if (isRightSwipe && activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  // Mouse swipe fonksiyonları
  const onMouseDown = (e: React.MouseEvent) => {
    setTouchEnd(null);
    setTouchStart(e.clientX);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (touchStart !== null) {
      setTouchEnd(e.clientX);
    }
  };

  const onMouseUp = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && activeTab < tabs.length - 1) {
      setActiveTab(activeTab + 1);
    }
    if (isRightSwipe && activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  return (
    <div id="insights" className="scroll-mt-[80px]">
      <section ref={sectionRef} className={`${hasActivated ? current.sectionBg : "bg-[#f9fafb]"} py-16 px-4 sm:px-6 transition-colors duration-700 ease-in-out`}>
        <AnimatedSection>
          <div className="max-w-7xl mx-auto mb-10 px-4">
            <h2 className="text-xl sm:text-2xl md:text-3xl mb-2"> {t("tabSection.title6")}</h2>
            <p className="text-base sm:text-xl md:text-2xl">{t("tabSection.title7")}</p>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={200}>
          <div className={`max-w-7xl mx-auto p-6 sm:p-10 rounded-xl shadow-md transition-colors duration-700 ease-in-out ${hasActivated ? current.contentBg : "bg-white"}`}>
            <div className="flex flex-col gap-8">
              {/* Desktop'ta sekme başlıkları */}
              <div className="hidden md:flex flex-row gap-4 justify-start pb-2">
                {tabs.map((tab, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTab(index)}
                    className={`flex-shrink-0 text-sm sm:text-base font-medium px-4 py-2 rounded transition-all duration-300 ${
                      activeTab === index ? `bg-white text-black shadow` : "text-black hover:text-black"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Mobilde kayan label'lar */}
              <div className="md:hidden overflow-hidden relative mb-4">
                <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(calc(-${activeTab * 150}px + 50vw - 75px))` }}>
                  {tabs.map((tab, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTab(i)}
                      className={`flex-shrink-0 text-sm font-medium px-3 py-2 rounded transition-all duration-300 mr-2 text-black ${
                        activeTab === i ? `bg-white shadow` : `bg-white/20`
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobilde kayan içerik - title'lar ve resimler */}
              <div
                className="md:hidden overflow-hidden relative cursor-grab active:cursor-grabbing"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
              >
                <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${activeTab * 100}%)` }}>
                  {tabs.map((tab, i) => (
                    <div key={i} className="min-w-full flex flex-col gap-4 px-4">
                      {/* Mobilde title */}
                      <div className="flex justify-center">
                        <h3 className={`text-lg font-bold leading-snug ${tab.textColor} text-center`}>{tab.title}</h3>
                      </div>

                      {/* Mobilde resim */}
                      <div className="w-full">
                        <img src={tab.image} alt="Görsel" className="w-full h-[300px] rounded-md object-cover" />
                      </div>

                      {/* Mobilde description */}
                      <div className="w-full">
                        <p className={`whitespace-pre-line font-medium text-sm leading-relaxed ${tab.textColor} text-center`}>{tab.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop'ta kayan içerik */}
              <div className="hidden md:block overflow-hidden relative">
                <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${activeTab * 100}%)` }}>
                  {tabs.map((tab, i) => (
                    <div key={i} className="min-w-full flex flex-row gap-6 sm:gap-10 px-4 sm:px-6 items-center">
                      <div className="w-1/2 px-1 sm:px-2">
                        <h3 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-snug ${tab.textColor}`}>{tab.title}</h3>
                        <p className={`whitespace-pre-line font-medium text-base sm:text-lg md:text-xl leading-relaxed mt-6 sm:mt-10 ${tab.textColor}`}>{tab.description}</p>
                      </div>

                      <div className="w-1/2">
                        <img src={tab.image} alt="Görsel" className="w-full h-[400px] sm:h-[500px] sm:w-[500px] rounded-md object-cover" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default TabSection;
